<template>
  <div class="grid-string grid-string-data" draggable="true" @dragover.prevent="handleDrag">
    <template v-for="field in fields">
      <component
        :is="field.component"
        :key="field.slug + field.upd"
        :editable="editable"
        v-bind="field.props"
        :total-price="total"
        :group-closed="groupClosed"
        :group-has-rows="groupHasRows"
        v-on="field.events"
        @contextmenu.native="emitContext($event, field)"
      />
    </template>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import RowMixin from '@/components/common/RowMixin';
  import IconsCell from '@/components/project/list/IconsCell';
  import EmptyCell from '@/components/project/list/EmptyCell';
  import RowContext from '@/components/project/list/contexts/Row';
  import copy from '@/utils/copy';
  import factory from '@/utils/api/factory';
  import GroupCell from '@/components/common/GroupCell';

  /**
   * Fast go to:
   * – {@link index_column} – first column with actions in form of icons
   * – {@link description_column} – powerful description cell
   * – {@link empty_column} – empty is a cell that takes space of many cells
   */

  export default {
    name: 'ListGroup',
    mixins: [RowMixin],

    props: {
      currency: {
        type: String,
        required: true,
      },
      cellAmount: Number,

      editable: {
        type: Boolean,
        required: true,
      },

      total: Number,
      groupClosed: {
        type: Boolean,
        default: false,
      },
      groupHasRows: {
        type: Boolean,
        default: true,
      },
    },

    data() {
      return {
        COMPONENTS: {
          /** @name index_column */
          index: {
            component: IconsCell,
            props: () => ({
              id: this.data.id,
              noButtons: !this.editable,
            }),
            events: (field) => ({
              add: () => this.$emit('add', this.data.order),
              options: (event) => this.emitContext(event, field),
              startDrag: () => this.$emit('start-drag'),
              endDrag: () => this.$emit('drop'),
            }),
          },

          /** @name description_column */
          description: {
            component: GroupCell,
            props: (field) => {
              return {
                label: this.data[field.slug],
                // preLabel: this.data.groupNo,
                currency: this.currency,
                class:
                  'cell-description cell-bold ' +
                  (this.data.group_level === 1 ? 'span-blue' : 'span-black'),
                index: this.row.groupNo,
              };
            },
            events: (field) => ({
              change: async ({ value }) => {
                const res = await factory('rows').updateInPath(this.data.id, {
                  field: field.slug,
                  value,
                });
                this.title = res.data[field.slug];
              },
              toggle: () => {
                this.$emit('toggle-group');
              },
              options: (event) => this.emitContext(event, field),
            }),
          },

          /** @name empty_column */
          empty: {
            component: EmptyCell,
            props: () => {
              return {
                style: `grid-column: span ${this.cellAmount - 2} / auto;`,
                count: this.cellAmount - 2,
              };
            },
            events: () => ({}),
          },
        },
      };
    },

    methods: {
      ...mapActions({
        openContext: 'context/open',
      }),
      handleDrag() {
        this.$emit('drag');
      },

      emitContext(event, field) {
        if (this.editable) this.rowOptions(event, field);
      },

      // Context menu with all actions suitable specially for the group row
      rowOptions(event, field = null) {
        event.preventDefault();
        const allowPaste = field && field.group.allowPaste;
        const allowCopy = field && field.group.allowCopy;
        this.openContext({
          // Default props to context
          pageX: event.pageX,
          pageY: event.pageY,
          type: event.type,
          component: RowContext,

          // Custom props to context
          allowPaste,
          allowCopy,
          allowComment: false,
          rowLevel: this.data.group_level,

          // Custom 'events' as props to context
          delete: () => this.$emit('delete', this.data.id),
          add: () => this.$emit('add', this.data.order),
          addGroup: (level) => this.$emit('addGroup', this.data.order, level),
          convertToGroup: (level) => this.$emit('convertToGroup', this.data.id, level),
          copy: () => copy(this.data?.[field?.slug]),
          paste: () => {
            navigator.clipboard.readText().then((clipText) => {
              const el = event.target;
              const children = el.children || [];
              if (children.length > 0) children[0].textContent = clipText.slice(0, 200);
              else el.textContent = clipText.slice(0, 200);
            });
          },
        });
      },
    },
  };
</script>
